import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { formatAssociatedPartyAddress } from '@/features/associated-parties/formatAddress'
import { ProductManufacturerSelect } from '@/features/product-catalog/components/ProductManufacturerSelect'
import { useListAssociatedParties } from '@/shared/hooks/api/associatedParties'
import {
  useDownloadRohsProductComplianceFileOutputDocument,
  useStartRohsProductComplianceFileDeclarationOfConformityGeneration,
  type RohsComponentComplianceFileView,
  type RohsProductComplianceFileOutputDocument,
  type RohsProductComplianceFileView,
} from '@/shared/hooks/api/rohsComplianceFiles'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { rohsProductComplianceFileDeclarationOfConformityProgressMessage } from '@/v2/lib/rohsProductComplianceFileDeclarationOfConformityProgressMessage'
import { evaluateRohsProductComplianceFileDeclarationOfConformityRequirements } from '@/v2/lib/rohsProductComplianceFileDeclarationOfConformityRequirements'
import { declarationOfConformityGenerateDialogContentClassName } from '@/v2/components/product-compliance-briefing/declarationOfConformityGenerateDialogContentClassName'
import { RohsProductComplianceFileOutputDocumentsSection } from './RohsProductComplianceFileOutputDocumentsSection'

const ASSOCIATED_PARTY_SELECT_ID = 'rohs-doc-associated-party'

type RohsProductComplianceFileDeclarationOfConformityGenerateDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: string
  productName: string | null | undefined
  productSku: string | null | undefined
  productDescription: string | null | undefined
  rohsApplies: boolean
  productFile: RohsProductComplianceFileView
  components: RohsComponentComplianceFileView[]
  outputDocuments: RohsProductComplianceFileOutputDocument[]
  onProgressMessage: (message: string | null) => void
}

export function RohsProductComplianceFileDeclarationOfConformityGenerateDialog({
  open,
  onOpenChange,
  productId,
  productName,
  productSku,
  productDescription,
  rohsApplies,
  productFile,
  components,
  outputDocuments,
  onProgressMessage,
}: RohsProductComplianceFileDeclarationOfConformityGenerateDialogProps) {
  const { t } = useTranslation('common')
  const { t: tAssociatedParties } = useTranslation('associatedParties')
  const { t: tCountries } = useTranslation('countries')
  const canWriteOrgData = useCanWriteOrgData()
  const [selectedPartyId, setSelectedPartyId] = useState('')
  const { data: associatedParties = [] } = useListAssociatedParties({
    enabled: open,
  })

  const generateMutation =
    useStartRohsProductComplianceFileDeclarationOfConformityGeneration(productId)
  const downloadMutation = useDownloadRohsProductComplianceFileOutputDocument(
    productId
  )

  useEffect(() => {
    if (!open) {
      setSelectedPartyId('')
    }
  }, [open])

  const selectedParty = useMemo(
    () => associatedParties.find((party) => party.id === selectedPartyId),
    [associatedParties, selectedPartyId]
  )

  const manufacturerPreview = useMemo(() => {
    if (selectedParty == null) {
      return null
    }
    return {
      name: selectedParty.name.trim(),
      address: formatAssociatedPartyAddress(selectedParty, {
        getCountryName: (code) => tCountries(code) || code,
        translate: (key, options) => tAssociatedParties(key, options),
      }).trim(),
    }
  }, [selectedParty, tAssociatedParties, tCountries])

  const { generateReady } =
    evaluateRohsProductComplianceFileDeclarationOfConformityRequirements({
      productName,
      productSku,
      productDescription,
      rohsApplies,
      productFile,
      components,
      manufacturerName: manufacturerPreview?.name,
      manufacturerAddress: manufacturerPreview?.address,
    })

  const isBusy = generateMutation.isPending || downloadMutation.isPending
  const hasInFlightOutputDocument = outputDocuments.some(
    (document) =>
      document.status === 'PENDING' || document.status === 'PROCESSING'
  )

  const canConfirm =
    canWriteOrgData &&
    generateReady &&
    manufacturerPreview != null &&
    manufacturerPreview.name.length > 0 &&
    manufacturerPreview.address.length > 0 &&
    !isBusy &&
    !hasInFlightOutputDocument

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isBusy) {
        return
      }
      onOpenChange(nextOpen)
    },
    [isBusy, onOpenChange]
  )

  const handleConfirm = async () => {
    if (!canWriteOrgData || manufacturerPreview == null || !generateReady) {
      return
    }
    onProgressMessage(
      t('v2.pages.products.detail.rohsFiles.declarationOfConformity.generating')
    )
    try {
      await generateMutation.mutateAsync({
        manufacturerName: manufacturerPreview.name,
        manufacturerAddress: manufacturerPreview.address,
        onEvent: (event) => {
          if (event.type === 'step.started') {
            const localizedMessage =
              rohsProductComplianceFileDeclarationOfConformityProgressMessage(
                event.step,
                t
              )
            if (localizedMessage != null) {
              onProgressMessage(localizedMessage)
            }
          }
        },
        onComplete: async (result) => {
          await downloadMutation.mutateAsync({
            outputDocumentId: result.outputDocumentId,
            fileName: result.fileName,
          })
        },
      })
      onOpenChange(false)
    } catch {
      toast.error(
        t(
          'v2.pages.products.detail.rohsFiles.declarationOfConformity.exportFailed'
        )
      )
    } finally {
      onProgressMessage(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={declarationOfConformityGenerateDialogContentClassName}
        showCloseButton={!isBusy}
        onEscapeKeyDown={(event) => {
          if (isBusy) {
            event.preventDefault()
          }
        }}
        onInteractOutside={(event) => {
          if (isBusy) {
            event.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t(
              'v2.pages.products.detail.rohsFiles.declarationOfConformity.exportDialog.title'
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              'v2.pages.products.detail.rohsFiles.declarationOfConformity.exportDialog.description'
            )}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          {!canWriteOrgData ? (
            <p className='text-sm text-muted-foreground'>
              {t(
                'v2.pages.products.detail.rohsFiles.declarationOfConformity.readOnlyHint'
              )}
            </p>
          ) : null}
          <div className='space-y-2'>
            <Label htmlFor={ASSOCIATED_PARTY_SELECT_ID}>
              {t(
                'v2.pages.products.detail.rohsFiles.declarationOfConformity.exportDialog.partyLabel'
              )}
            </Label>
            <p className='text-xs text-muted-foreground'>
              {t(
                'v2.pages.products.detail.rohsFiles.declarationOfConformity.exportDialog.partyDescription'
              )}
            </p>
            <ProductManufacturerSelect
              value={selectedPartyId}
              onValueChange={setSelectedPartyId}
              disabled={isBusy || !canWriteOrgData}
              enabled={open}
              inForm={false}
              triggerId={ASSOCIATED_PARTY_SELECT_ID}
            />
          </div>
          {manufacturerPreview != null ? (
            <div className='space-y-2 rounded-md border bg-muted/30 p-3 text-sm'>
              <p className='font-medium'>
                {t(
                  'v2.pages.products.detail.rohsFiles.declarationOfConformity.exportDialog.previewTitle'
                )}
              </p>
              <p>{manufacturerPreview.name}</p>
              <p className='text-muted-foreground'>
                {manufacturerPreview.address}
              </p>
            </div>
          ) : null}
          <div className='space-y-2'>
            <p className='text-sm font-medium'>
              {t(
                'v2.pages.products.detail.rohsFiles.declarationOfConformity.exportDialog.issuedDocumentsTitle'
              )}
            </p>
            <RohsProductComplianceFileOutputDocumentsSection
              productId={productId}
              outputDocuments={outputDocuments}
            />
          </div>
          {hasInFlightOutputDocument ? (
            <p className='text-xs text-muted-foreground' aria-live='polite'>
              {t(
                'v2.pages.products.detail.rohsFiles.outputDocuments.generatingHint'
              )}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            disabled={isBusy}
            onClick={() => {
              onOpenChange(false)
            }}
          >
            {t(
              'v2.pages.products.detail.rohsFiles.declarationOfConformity.exportDialog.cancel'
            )}
          </Button>
          <Button
            type='button'
            disabled={!canConfirm}
            onClick={() => {
              void handleConfirm()
            }}
          >
            {isBusy ? <Loader2 className='size-4 animate-spin' /> : null}
            {t(
              'v2.pages.products.detail.rohsFiles.declarationOfConformity.exportDialog.confirm'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
